import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

import { destinations as destinationSelectorData } from '@awaze/design-system.compositions.form-fields'
import { Region } from '@awaze/design-system.compositions.search-form/dist/search-form.types'
import { GoogleSearchMethods } from '@awaze/mfe-common.api.places-api'
import { Destination } from '@awaze/mfe-common.api.places-api/dist/types'
import {
  DataLayerEventType,
  DataLayerFactory,
  TmsContextType,
} from '@awaze/mfe-common.contexts.tms'
import { generateSearchUrl } from '@awaze/mfe-common.utils.search-url'
import { Brand } from '@awazevr/design-system.components.awaze-theme-provider'

import { OPTIMIZELY_FLAGS_ENUM, OPTIMIZELY_VARIANT_KEYS } from '@/constants'
import { useOptimizelyDecisionsContext } from '@/context/optimizely-decisions-context'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'
import { cleanObjectOf } from '@/utils/helpers'

import {
  FUSION_DATE_FORMAT,
  PlacesSearchConfigType,
  SearchFormSubmitData,
} from './SearchForm.types'

export const handleSearchFormSubmit = (
  brand: Brand,
  formData: SearchFormSubmitData,
  tms: TmsContextType,
  placesConfig: PlacesSearchConfigType,
): void => {
  const {
    destination,
    destinationURL,
    maxPrice,
    minPrice,
    radiusCentre: formDataRadiusCentre,
    range,
    start,
    placesId,
    ...rest
  } = formData

  const radiusCentre = formDataRadiusCentre ?? undefined

  const isGoogleDestination = !!radiusCentre

  const cleanedRest = cleanObjectOf({
    data: rest,
    filterOutValues: [null, undefined, 0, ''],
  })

  if (isGoogleDestination) {
    delete cleanedRest.id
  }

  const selectedPopularDestination = destinationSelectorData.flatMap(
    (destinationBlock: Region) =>
      destinationBlock.popular?.locations.filter(
        (location) => location.regionName === destination,
      ) || [],
  )[0]

  const searchParams = {
    ...(!isGoogleDestination && {
      placesId: placesId,
    }),
    regionName: selectedPopularDestination
      ? selectedPopularDestination.regionName
      : destination,
    destinationURL,
    maxPrice: start && maxPrice?.toString(),
    minPrice: start && minPrice?.toString(),
    radiusCentre,
    range,
    start: start && format(parseISO(start), FUSION_DATE_FORMAT),
    ...cleanedRest,
  }

  /**
   * If a map area search has been done then a new location has been specified for a subsequent search, remove map area search params (ne, sw)
   */
  if (!destination || isGoogleDestination || placesId) {
    delete searchParams.ne
    delete searchParams.sw
  }

  new DataLayerFactory().getEvent(DataLayerEventType.SearchSubmit).update(
    tms,
    {
      ...formData,
      ...(isGoogleDestination && {
        googleSearch: `google places - ${placesConfig.googleFallbackMethod}`,
      }),
    },
    dataLayerErrorHandler,
  )

  globalThis.location.href = generateSearchUrl(searchParams, brand)
}

export const handleSearchFormSummaryClick = (tms: TmsContextType): void => {
  new DataLayerFactory()
    .getEvent(DataLayerEventType.SearchStart)
    .update(tms, {}, dataLayerErrorHandler)
}

const destinationIdentifierFromLocale = (
  locale?: string,
):
  | 'da'
  | 'de'
  | 'de-at'
  | 'de-ch'
  | 'en'
  | 'en-us'
  | 'es'
  | 'fi'
  | 'fr'
  | 'hr'
  | 'hu'
  | 'it'
  | 'nb'
  | 'nl'
  | 'nl-be'
  | 'pl'
  | 'sv' => {
  switch (locale?.toLocaleLowerCase() || '') {
    case 'da':
    case 'da-dk':
      return 'da'

    case 'de':
    case 'de-de':
      return 'de'

    case 'de-at':
      return 'de-at'

    case 'de-ch':
      return 'de-ch'

    case 'en':
    case 'en-gb':
      return 'en'

    case 'en-us':
      return 'en-us'

    case 'es':
      return 'es'

    case 'fi':
      return 'fi'

    case 'fr':
      return 'fr'

    case 'hr':
      return 'hr'

    case 'hu':
      return 'hu'

    case 'it':
      return 'it'

    case 'nb':
      return 'nb'

    case 'nl':
    case 'nl-nl':
      return 'nl'

    case 'nl-be':
      return 'nl-be'

    case 'pl':
      return 'pl'

    case 'sv':
      return 'sv'
  }
  throw new Error(`No destinations exist for unexpected locale ${locale}.`)
}

export const getHardcodedDestinationData = (
  locale: string,
  id?: string,
): Promise<Destination> => {
  const fileIdentifier = destinationIdentifierFromLocale(locale)

  const response = fetch(
    `/api/search/destinations?fileIdentifier=${fileIdentifier}&id=${id ?? 'countries'}`,
  )

  return response.then((res) => res.json()).catch(() => {})
}

/**
 * Determine which Google fallback method to use for the given brand.
 * We can run 3 xp's. 1 for each brand, so we can turn it off per brand.
 */
export function useGoogleFallback(brand: Brand): GoogleSearchMethods | null {
  const optiContext = useOptimizelyDecisionsContext()
  let FLAG = null

  if (brand === 'cottages.com') return 'autocomplete'

  switch (brand) {
    case 'hoseasons':
      FLAG = OPTIMIZELY_FLAGS_ENUM.AGE_ACQUISITION_GOOGLE_FALLBACK_HOS
      break
    case 'novasol':
      FLAG = OPTIMIZELY_FLAGS_ENUM.AGE_ACQUISITION_GOOGLE_FALLBACK_NOV
      break
  }

  if (!FLAG) {
    return null
  }

  const variationKey = optiContext?.getExperimentVariationKey(FLAG)
  if (!variationKey) {
    return null
  }

  switch (variationKey) {
    case OPTIMIZELY_VARIANT_KEYS.GOOGLE_FALLBACK_AUTOCOMPLETE:
      return 'autocomplete'
    case OPTIMIZELY_VARIANT_KEYS.GOOGLE_FALLBACK_TEXTSEARCH:
      return 'textsearch'
    case 'baseline':
      return null
    default:
      return null
  }
}
